import { SyntheticEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Button,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';

import logger from '../utils/logger';
import * as AuthService from '../services/emailOTPAuth';
import { getResponseUrl } from '../services/emailOTPAuth';
import { OTP_RETRY_COOKIE } from '../constants/cookies';
import { AppConfig } from '../types/app';
import { validateEmail } from '../utils/email';
import { getInitialLocaleSelection } from '../services/locale';
import usePageTitle from '../utils/usePageTitle';
import otpMessages from '../i18n/otp.messages';
import challengeMessages from '../i18n/answerChallenge.messages';
import TopHeader from '../components/TopHeader';
import ContainerLayout from '../components/ContainerLayout';
import Main from '../components/Main';
import Footer from '../components/Footer';
import HelpLink from '../components/HelpLink';
import { AnswerChallengeForm } from '../components/AnswerChallengeForm';
import styles from '../components/CardContainer.module.css';
import { OtpPhase } from '../constants/otpPhase';

function CombinedOTPSignIn({ config }: { config: AppConfig }) {
  if (config.enableCombinedOtp !== 'true') {
    throw new Error('Invalid path');
  }

  const { formatMessage } = useIntl();
  usePageTitle('One-time email passcode');
  const location = useLocation();
  const navigate = useNavigate();

  const hState = location.state as any;
  const storedEmail = sessionStorage.getItem('otp_prefill_email');
  if (storedEmail) sessionStorage.removeItem('otp_prefill_email');
  const prefillEmail = hState?.userEmail || storedEmail || '';

  const [emailValue, setEmailValue] = useState(prefillEmail);
  const [emailEntered, setEmailEntered] = useState(validateEmail(prefillEmail));
  const [emailFormatError, setEmailFormatError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<OtpPhase>(
    validateEmail(prefillEmail) ? OtpPhase.AutoSending : OtpPhase.EmailInput
  );

  // OTP phase state — set after successful passcode send
  const [cognitoUserName, setCognitoUserName] = useState('');
  const [cognitoSession, setCognitoSession] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const enableCookieComponent = config?.enableCookieComponent === 'true';

  // Handle OTP retry — skip passcode flow and use cached tokens
  const [isRetrying, setIsRetrying] = useState(false);
  useEffect(() => {
    const isOtpRetry = Cookies.get(OTP_RETRY_COOKIE) === 'true';
    if (isOtpRetry) {
      setIsRetrying(true);
      Cookies.remove(OTP_RETRY_COOKIE);
      const urlParams = new URLSearchParams(location.search);
      getResponseUrl(
        config,
        'invalidIdToken_useCached',
        'invalidAccessToken_useCached',
        urlParams.get('redirect_uri')!,
        urlParams.get('state')!
      )
        .then((responseUrl) => window.location.assign(responseUrl))
        .catch(() => setIsRetrying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendPasscode = async (email: string) => {
    if (!validateEmail(email)) {
      setEmailFormatError(formatMessage(otpMessages.emailFormatError));
      return;
    }
    try {
      setLoading(true);
      const isNew = await AuthService.newUser(email);
      if (isNew) await AuthService.signUp(email);
      const cognitoUser = await AuthService.startSession(email);
      await AuthService.answerLocaleChallenge(
        getInitialLocaleSelection(),
        cognitoUser
      );
      setCognitoUserName(cognitoUser.getUsername());
      setCognitoSession((cognitoUser as any).Session);
      setUserEmail(email);
      setPhase(OtpPhase.OtpReady);
      setLoading(false);
    } catch (err) {
      setEmailFormatError(formatMessage(otpMessages.emailOTPError));
      setPhase(OtpPhase.EmailInput);
      setLoading(false);
      logger.error(err);
    }
  };

  useEffect(() => {
    AuthService.init(config);
  }, [config]);

  // Auto-send passcode if email is prefilled
  useEffect(() => {
    if (prefillEmail && validateEmail(prefillEmail)) {
      handleSendPasscode(prefillEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    await handleSendPasscode(emailValue);
  };

  const handleEmailChange = (e: any) => {
    const val = e.detail.value;
    setEmailEntered(validateEmail(val));
    if (validateEmail(val)) setEmailFormatError('');
    setEmailValue(val);
  };

  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <ContainerLayout enableResponsiveStyles>
          <ContentLayout>
            {isRetrying || phase === OtpPhase.AutoSending ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Spinner size="large" data-testid="spinner" />
              </div>
            ) : phase === OtpPhase.OtpReady ? (
              <>
                <AnswerChallengeForm
                  config={config}
                  headerMsg={challengeMessages.header}
                  descriptionMsg={challengeMessages.description}
                  showBackButton={true}
                  useUser={{
                    userName: cognitoUserName,
                    session: cognitoSession,
                    email: userEmail,
                  }}
                />
                <HelpLink />
              </>
            ) : (
              <>
                <Container
                  header={
                    <Header
                      className={styles.header}
                      variant="h2"
                      headingTagOverride="h1"
                      description={formatMessage(otpMessages.description)}
                    >
                      {formatMessage(otpMessages.header)}
                    </Header>
                  }
                >
                  <form onSubmit={handleEmailSubmit}>
                    <Form
                      actions={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            data-testid="back-button"
                            onClick={() => navigate(-1)}
                            formAction="none"
                            disabled={loading}
                          >
                            {formatMessage(otpMessages.previousButtonLabel)}
                          </Button>
                          <Button
                            variant="primary"
                            data-testid="submit-button"
                            disabled={!emailEntered}
                            formAction="submit"
                            loading={loading}
                          >
                            {formatMessage(otpMessages.buttonLabel)}
                          </Button>
                        </SpaceBetween>
                      }
                    >
                      <FormField
                        errorText={emailFormatError}
                        stretch
                        label={formatMessage(otpMessages.emailInputName)}
                      >
                        <Input
                          name="email"
                          type="email"
                          data-testid="text-input"
                          value={emailValue}
                          onChange={handleEmailChange}
                          disabled={loading}
                          disableBrowserAutocorrect
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                          className={styles.mobileInput}
                          autoComplete="email"
                        />
                      </FormField>
                    </Form>
                  </form>
                </Container>
                <HelpLink />
              </>
            )}
          </ContentLayout>
        </ContainerLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
}

export default CombinedOTPSignIn;
